// side effect: start session when featureModel changes
import {
	fetchConditionalVariantValues,
	fetchProductData,
	fetchTranslations,
	fetchVariantCodeTypeTemplate,
	initDefaults
} from '../slices/productDataSlice';
import {startSession} from '../slices/sessionSlice';
import {fetchFeatureModel} from '../slices/featureModelSlice';
import {decisions, fetchConsequences, fetchDecisions, resetDecisions} from '../slices/decisionsSlice';
import {mergeWithProperty} from '../util/mergeWithProperty';
import {regionToFeatureValueMap} from '../lightProfiles';
import {clearShortNumberConfig} from '../slices/shortNumberSlice';

export const featureModelMiddleware = store => next => async action => {
	const result = next(action); // execute middleware after action

	if (action.type === 'configuration/setFeatureModel') {

		// orchestrate configuration engine data and session init

		// get data for selected feature model
		store.dispatch(fetchTranslations());
		await Promise.all([
			store.dispatch(fetchConditionalVariantValues()),
			store.dispatch(fetchProductData()),
			store.dispatch(fetchVariantCodeTypeTemplate())
		]);

		// prepare default decisions based on conditionalVariantValues and productData
		store.dispatch(initDefaults({region: store.getState().status.region}));

		// start new session and get the featureModel data
		await store.dispatch(startSession());
		await store.dispatch(fetchFeatureModel());

		// figure out initial decisions (init, defaults, loaded via configuration or shortNumber)

		// replay decisions (from defaults and stored data)
		const defaults = store.getState().productData.defaults.filter(
			(conditionalVariantValue, index, ccvs) =>
				ccvs.findIndex(t => (t.variableId === conditionalVariantValue.variableId)) === index
		);

		const decisionDecisions = store.getState().decisions?.data?.decisions;
		const configurationDecisions = store.getState().configuration?.data?.projects[0]?.project?.decisions?.features;
		const shortnumberDecisions = store.getState().shortNumber?.data?.loadedConfig?.configurationData?.decisions.map(
			decision => ({
				variableId: decision.variableId,
				featureValues: [
					{
						featureValueId: decision.featureValue,
						state: 'Included',
						kind: 'Explicit'
					}
				]
			})
		);

		let loaded = decisionDecisions.length > 0 ? decisionDecisions : configurationDecisions ?? [];
		if (configurationDecisions.length === 0) loaded = mergeWithProperty(defaults, loaded, 'variableId');

		if (shortnumberDecisions && shortnumberDecisions.length > 0) loaded = mergeWithProperty(defaults, shortnumberDecisions, 'variableId');

		const regionDecision = [
			{
				variableId: 'CX_Region',
				featureValues: [
					{
						featureValueId: regionToFeatureValueMap[store.getState().status.region],
						state: 'Included',
						kind: 'Explicit'
					}
				]
			},
		];
		const loadedWithRegion = mergeWithProperty(loaded, regionDecision, 'variableId');
		// only apply default when loaded decisions are empty
		const initialDecisions = loaded.length > 0 ? loaded : mergeWithProperty(defaults, loadedWithRegion, 'variableId');

		await store.dispatch(clearShortNumberConfig());
		await store.dispatch(resetDecisions());
		await store.dispatch(decisions(
			{
				features: initialDecisions,
				attributes: []
			}
		));

		store.dispatch(fetchDecisions());
		store.dispatch(fetchConsequences());

	}

	return result;
};
